import { HttpClient } from '@angular/common/http';
import { Injectable, effect, signal } from '@angular/core';
import { tap } from 'rxjs';
import { API_BASE_URL } from '../config/api.config';
import type { Product } from '../models/product.model';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class WishlistService {
  readonly wishlistedIds = signal<ReadonlySet<string>>(new Set());

  constructor(
    private readonly http: HttpClient,
    private readonly authService: AuthService,
  ) {
    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.refresh();
      } else {
        this.wishlistedIds.set(new Set());
      }
    });
  }

  refresh(): void {
    this.http
      .get<Product[]>(`${API_BASE_URL}/users/me/wishlist`)
      .subscribe((products) => {
        this.wishlistedIds.set(new Set(products.map((p) => p.id)));
      });
  }

  isSaved(productId: string): boolean {
    return this.wishlistedIds().has(productId);
  }

  toggle(productId: string) {
    return this.isSaved(productId)
      ? this.remove(productId)
      : this.add(productId);
  }

  private add(productId: string) {
    return this.http
      .post(`${API_BASE_URL}/wishlist/${productId}`, {})
      .pipe(
        tap(() => {
          const next = new Set(this.wishlistedIds());
          next.add(productId);
          this.wishlistedIds.set(next);
        }),
      );
  }

  private remove(productId: string) {
    return this.http.delete(`${API_BASE_URL}/wishlist/${productId}`).pipe(
      tap(() => {
        const next = new Set(this.wishlistedIds());
        next.delete(productId);
        this.wishlistedIds.set(next);
      }),
    );
  }
}
